import { Quaternion } from '@technology/web3d';
import {
  PlanarCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import { SyncFn } from './sync-coordinator';
import {
  getActiveNavCap,
  navigateToClosestStation,
  updateFrustum,
} from './sync-source-helpers';
import {
  getStationSyncPosition,
  isStationOrientationMeaningful,
} from './source-positions';

/**
 * Syncs between two station/image panes.
 * Reads the station position from source, finds the closest station in
 * target, and navigates to it. Propagates source quaternion + fov when
 * source orientation is meaningful (not Planar) and target is not Planar.
 */
export const syncStationToStation: SyncFn = async (source, target) => {
  const syncPosition = getStationSyncPosition(source);
  if (!syncPosition) return;

  const sourceViewer = source.viewerWrapper()?.viewer;
  const targetViewer = target.viewerWrapper()?.viewer;
  if (!sourceViewer || !targetViewer) return;

  const targetCap = getActiveNavCap(target);
  if (!(targetCap instanceof StationsNavigationCapability)) return;

  const stationsProvider = target.get(StationsCapabilityProvider)();
  if (!stationsProvider) return;

  navigateToClosestStation(
    syncPosition,
    targetCap,
    stationsProvider,
    targetViewer
  );

  const propagateOrientation =
    isStationOrientationMeaningful(source) &&
    !(targetCap instanceof PlanarCapability);

  if (propagateOrientation) {
    targetCap.applyCameraOrientation(
      new Quaternion(...sourceViewer.camera.quaternion.toArray()),
      sourceViewer.camera.fieldOfView
    );
  }

  updateFrustum(
    target,
    syncPosition,
    propagateOrientation ? sourceViewer.camera.quaternion.toArray() : null,
    propagateOrientation ? sourceViewer.camera.fieldOfView : null
  );
};
